import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const DEFAULT_DOMAIN = 'https://smartresourcesqld.com.au';
const DEFAULT_FALLBACK_IMAGE = 'https://smartresourcesqld.com.au/static/media/smart-banner-2.09079fa08d21da7d6cbb.webp';

const MetaTags = ({ 
  title, 
  description, 
  keywords,
  identifyUrl,
  DCTitle,
  DCDescription,
  DCSubject,
  Canonical,
  canonical,
  CanonicalUrl,
  href,
  ogTitle, 
  ogDescription, 
  ogImage, 
  ogUrl,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  robots = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
}) => {
  const pageTitle = title || DCTitle || 'Business Consulting in Queensland | Smart Resources QLD';
  const pageDescription = description || DCDescription || 'Empowering Queensland businesses with tailored solutions in synthetic data, angel investment, hospitality, management consulting, and software development.';
  const pageKeywords = keywords || '';
  
  // Resolve canonical / page URL
  const rawUrl = Canonical || canonical || CanonicalUrl || href || ogUrl || identifyUrl || DEFAULT_DOMAIN;
  const pageUrl = typeof rawUrl === 'string' && rawUrl.startsWith('/') 
    ? `${DEFAULT_DOMAIN}${rawUrl}` 
    : (rawUrl || DEFAULT_DOMAIN);

  // Resolve OG / Twitter title and description
  const finalOgTitle = ogTitle || DCTitle || pageTitle;
  const finalOgDescription = ogDescription || DCDescription || pageDescription;
  const finalTwitterTitle = twitterTitle || finalOgTitle;
  const finalTwitterDescription = twitterDescription || finalOgDescription;

  // Resolve image URL (handle imported relative paths like /static/media/...)
  let rawImage = ogImage || twitterImage || DEFAULT_FALLBACK_IMAGE;
  if (typeof rawImage === 'object' && rawImage !== null && rawImage.default) {
    rawImage = rawImage.default;
  }
  const pageImage = typeof rawImage === 'string' && rawImage.startsWith('/')
    ? `${DEFAULT_DOMAIN}${rawImage}`
    : (rawImage || DEFAULT_FALLBACK_IMAGE);

  useEffect(() => {
    if (!pageTitle) return;

    document.title = pageTitle;

    if (window.Tawk_API) {
      window.Tawk_API.disableTitleNotification = true;
    }

    const targetNode = document.querySelector('title');
    if (!targetNode) return;

    const observer = new MutationObserver(() => {
      if (document.title !== pageTitle) {
        document.title = pageTitle;
      }
    });

    observer.observe(targetNode, {
      subtree: true,
      characterData: true,
      childList: true
    });

    return () => {
      observer.disconnect();
    };
  }, [pageTitle]);

  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {pageKeywords ? <meta name="keywords" content={pageKeywords} /> : null}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots.includes('noindex') ? 'noindex, nofollow' : 'index, follow, archive'} />
      <meta name="msnbot" content={robots.includes('noindex') ? 'noindex, nofollow' : 'index, follow, archive'} />
      <meta name="Slurp" content={robots.includes('noindex') ? 'noindex, nofollow' : 'index, follow, archive'} />
      <meta name="organization" content="Smart Resources QLD" />
      <meta name="identifier-url" content={pageUrl} />
      <link rel="canonical" href={pageUrl} />

      {/* Dublin Core Tags */}
      <meta name="DC.Title" content={DCTitle || pageTitle} />
      <meta name="DC.Description" content={DCDescription || pageDescription} />
      {DCSubject ? <meta name="DC.Subject" content={DCSubject} /> : null}
      <meta name="DC.Language" content="en" />
      <meta name="DC.Creator" content="Smart Resources QLD" />

      {/* Open Graph / Facebook Tags */}
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:site_name" content="Smart Resources QLD" />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:secure_url" content={pageImage} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={pageImage} />
      <meta name="twitter:url" content={pageUrl} />
    </Helmet>
  );
};

export default MetaTags;
